import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogOut, Menu, MessageCircle, User } from "lucide-react";

import { messagesApi } from "@/api/messages";
import MenuDrawer from "@/components/MenuDrawer";
import NotificationBell from "@/components/NotificationBell";

const Navbar = ({ user, login, logout }) => {
  const [unreadCount, setUnreadCount] = useState(0);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const navigate = useNavigate();

  const fetchUnreadCount = async () => {
    try {
      const data = await messagesApi.unreadCount();
      setUnreadCount(data.unread_count);
    } catch (e) {
      // ignore
    }
  };

  useEffect(() => {
    if (!user) return;
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, [user]);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-[#050505]/90 backdrop-blur-xl border-b border-white/5">
      <div className="max-w-7xl mx-auto px-4 md:px-12">
        <div className="flex items-center justify-between h-16">
          {/* Left: Menu + Logo */}
          <div className="flex items-center gap-3">
            <button
              onClick={() => setDrawerOpen(true)}
              className="inline-flex items-center justify-center w-9 h-9 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 text-[#EDEDED] transition-colors"
              aria-label="Open menu"
              data-testid="menu-drawer-btn"
            >
              <Menu className="w-4 h-4" />
            </button>
            <Link to="/" className="text-2xl text-[#EDEDED]" style={{ fontFamily: "'Playfair Display', serif" }} data-testid="logo-link">
              Breedling
            </Link>
          </div>

          {/* Right: Actions */}
          <div className="flex items-center gap-3 md:gap-4">
            <Link 
              to="/subscription-plans" 
              className="hidden sm:inline-flex text-xs px-3 py-1.5 rounded-lg bg-[#D4AF37]/10 text-[#D4AF37] hover:bg-[#D4AF37]/20 border border-[#D4AF37]/20 transition-colors font-medium"
              data-testid="plans-link"
            >
              Plans
            </Link>
            {user ? (
              <>
                <Link to="/messages" className="relative text-[#A1A1AA] hover:text-[#D4AF37] transition-colors" data-testid="messages-link">
                  <MessageCircle className="w-5 h-5" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#D4AF37] text-black text-xs rounded-full flex items-center justify-center">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </Link>
                <NotificationBell />
                <Link to="/dashboard" className="text-[#A1A1AA] hover:text-[#D4AF37] transition-colors flex items-center gap-2 text-sm" data-testid="dashboard-link">
                  <User className="w-4 h-4" />
                  <span className="hidden sm:inline">{user.name?.split(" ")[0]}</span>
                </Link>
                <button onClick={logout} className="text-[#A1A1AA] hover:text-red-400 transition-colors" data-testid="logout-btn">
                  <LogOut className="w-4 h-4" />
                </button>
              </>
            ) : (
              <button onClick={() => navigate('/signin')} className="btn-gold text-xs px-4 py-2" data-testid="signin-btn">
                Sign In
              </button>
            )}
          </div>
        </div>
      </div>

      <MenuDrawer open={drawerOpen} onOpenChange={setDrawerOpen} />
    </nav>
  );
};

export default Navbar;
